import { useCallback, useEffect, useReducer, useState } from "react";

type HTMLState = { isActive: boolean; isFocused: boolean; isHovered: boolean };

const emptyState: HTMLState = {
  isActive: false,
  isFocused: false,
  isHovered: false,
};
export const useHtmlStates = (): [
  state: HTMLState,
  ref: (v: HTMLElement | null) => void
] => {
  const [element, setElement] = useState<HTMLElement | null>(null);

  const ref = useCallback(setElement, [setElement]);

  useEffect(() => {
    if (element !== null) {
      element.addEventListener("focusin", onFocus);
      element.addEventListener("focusout", onBlur);

      element.addEventListener("mousedown", onMouseDown);
      element.addEventListener("mouseup", onMouseUp);

      element.addEventListener("mouseenter", onMouseEnter);
      element.addEventListener("mouseleave", onMouseLeave);

      return () => {
        element.removeEventListener("focusin", onFocus);
        element.removeEventListener("focusout", onBlur);

        element.removeEventListener("mousedown", onMouseDown);
        element.removeEventListener("mouseup", onMouseUp);

        element.removeEventListener("mouseenter", onMouseEnter);
        element.removeEventListener("mouseleave", onMouseLeave);
      };
    }
  }, [element]);

  const [htmlState, dispatch] = useReducer<
    (
      prevState: HTMLState,
      action: { type: string; payload: boolean }
    ) => HTMLState
  >((baseState, { type, payload }) => {
    const state = { ...baseState };
    switch (type) {
      case "ACTIVE": {
        state.isActive = payload;
        break;
      }
      case "FOCUS": {
        state.isFocused = payload;
        break;
      }
      case "HOVER": {
        state.isHovered = payload;
        break;
      }
    }
    return state;
  }, emptyState);

  const onFocus = () => {
    dispatch({ type: "FOCUS", payload: true });
  };
  const onBlur = () => {
    dispatch({ type: "FOCUS", payload: false });
  };

  const onMouseDown = () => {
    dispatch({ type: "ACTIVE", payload: true });
  };
  const onMouseUp = () => {
    dispatch({ type: "ACTIVE", payload: false });
  };

  const onMouseEnter = () => {
    dispatch({ type: "HOVER", payload: true });
  };
  const onMouseLeave = () => {
    dispatch({ type: "HOVER", payload: false });
  };

  return [htmlState, ref];
};
